/**
 * O que a tela apura da escala de um mês já carregado.
 *
 * Papel: aqui ficam `pendenciasDaEscala`, o filtro que decide quais pendências
 * de `conflitosDoDia` a grade, a caixa do dia e a lista de conflitos do mês
 * mostram, `pendenciasDoMes`, que junta as do mês inteiro, `folgasNoMes`, a
 * contagem de folgas de cada pessoa, `fraseDaPendencia`, o texto de cada uma, e
 * `comoChamar`, o nome curto com que uma pessoa aparece na grade e na folha, e
 * `aplicarEscalaPadrao` com `linhaDaEscalaPadrao`, o "↺ Escala padrão" da
 * caixa do dia e a linha "Padrão: …" debaixo dele, e `candidatosAEscala` com
 * `quemEntraNaEscala`, o campo "Adicionar nome..." da coluna.
 *
 * Como usar: todas recebem o que as consultas já trouxeram e não pedem nada à
 * API. `dias` de `folgasNoMes` e de `pendenciasDoMes` deverá ser o recorte do
 * mês, e não o da grade que a tela carrega: com os dias vizinhos junto, as duas
 * contariam folgas e conflitos de fora do mês mostrado.
 *
 * Um dia em que ninguém foi escalado não tem pendência nenhuma. `conflitosDoDia`
 * cobra situação de cada pessoa da casa em todo dia que não vale para todos, e
 * no dia de família com a escala em branco — que é como esse dia costuma ser
 * marcado — as pessoas da casa inteira apareceriam como pendentes de uma vez.
 * Só com alguém escalado a falta dos outros é esquecimento, e é esse o dia que o
 * aviso existe para trazer de volta.
 */
import {
  type Ausencia,
  type Conflito,
  type DiaCalendario,
  type EscalaPadraoDoDia,
  type PessoaEscalada,
  type StatusDiaCalendario,
  cobreODia,
  conflitosDoDia,
} from '@concierge/shared';

import { type PessoaDaEscala } from './types';

/**
 * As pendências da escala de um dia, como a tela as mostra.
 *
 * @param entrada - O dia a conferir, no mesmo formato de `conflitosDoDia`:
 *   `status` e `escalados` do dia, `deFerias` com quem tem ausência cobrindo a
 *   data e `daCasa` com quem trabalha na casa.
 * @returns As pendências de `conflitosDoDia`, ou nenhuma quando `escalados`
 *   está vazio.
 */
export function pendenciasDaEscala(entrada: {
  status: readonly StatusDiaCalendario[];
  escalados: readonly PessoaEscalada[];
  deFerias: readonly string[];
  daCasa: readonly string[];
}): Conflito[] {
  if (entrada.escalados.length === 0) return [];
  return conflitosDoDia(entrada);
}

/** As pendências de um dia do mês, com a data e quem está escalado nele. */
export interface PendenciasDoDia {
  /** A data em `AAAA-MM-DD`. */
  dia: string;
  /** Quem está escalado no dia, para `fraseDaPendencia` saber em qual lista. */
  escalados: readonly PessoaEscalada[];
  pendencias: Conflito[];
}

/**
 * As pendências de todos os dias do mês, na ordem das datas.
 *
 * @param dias - Os dias com registro do mês mostrado.
 * @param ausencias - As ausências que encostam no mês.
 * @param daCasa - Os identificadores de quem trabalha na casa.
 * @returns Só os dias que têm alguma pendência; vazio quando o mês está
 *   coerente.
 */
export function pendenciasDoMes(
  dias: readonly DiaCalendario[],
  ausencias: readonly Ausencia[],
  daCasa: readonly string[],
): PendenciasDoDia[] {
  return [...dias]
    .sort((a, b) => a.dia.localeCompare(b.dia))
    .flatMap((dia) => {
      const pendencias = pendenciasDaEscala({
        status: dia.status,
        escalados: dia.escalados,
        deFerias: ausencias.filter((a) => cobreODia(a, dia.dia)).map((a) => a.colaborador.id),
        daCasa,
      });
      return pendencias.length === 0
        ? []
        : [{ dia: dia.dia, escalados: dia.escalados, pendencias }];
    });
}

/**
 * Quantas folgas uma pessoa tem no mês.
 *
 * Um dia `folga_total` conta para todo mundo sem que ninguém apareça na lista
 * de folga dele, e por isso conta uma vez para cada pessoa; nos outros dias,
 * conta quem está escalado com o papel `folga`.
 *
 * @param colaboradorId - A pessoa cujas folgas se contam.
 * @param dias - Os dias com registro do mês mostrado.
 * @returns O número de dias de folga, zero quando não há nenhum.
 */
export function folgasNoMes(colaboradorId: string, dias: readonly DiaCalendario[]): number {
  return dias.filter(
    (dia) =>
      dia.status.includes('folga_total') ||
      dia.escalados.some((p) => p.colaboradorId === colaboradorId && p.papel === 'folga'),
  ).length;
}

/**
 * O que uma pendência diz, depois do nome de quem está nela.
 *
 * O nome fica de fora para quem monta a frase decidir como destacá-lo. A
 * pessoa de férias escalada é dita pela lista em que aparece, e é por isso que
 * a função recebe quem está escalado no dia.
 *
 * @param pendencia - A pendência, como `conflitosDoDia` a devolve.
 * @param escalados - Quem está escalado no dia da pendência.
 * @returns O resto da frase, como `está de férias mas consta no Plantão`.
 */
export function fraseDaPendencia(
  pendencia: Conflito,
  escalados: readonly PessoaEscalada[],
): string {
  if (pendencia.tipo === 'plantao-e-folga') return 'está em Plantão e Folga ao mesmo tempo';
  if (pendencia.tipo === 'sem-situacao') {
    return 'não tem situação definida (sem Plantão, Folga ou Férias)';
  }
  const papel = escalados.find((p) => p.colaboradorId === pendencia.colaboradorId)?.papel;
  return papel === 'folga'
    ? 'está de férias mas consta na Folga'
    : 'está de férias mas consta no Plantão';
}

/**
 * Como uma pessoa é chamada na célula, onde só cabe o nome curto.
 *
 * @param pessoa - Quem aparece na célula, com o apelido quando houver.
 * @returns O apelido, ou o primeiro nome de quem não tem apelido.
 */
export function comoChamar(pessoa: { nomeCompleto: string; apelido: string | null }): string {
  return pessoa.apelido ?? pessoa.nomeCompleto.split(' ')[0] ?? pessoa.nomeCompleto;
}

/**
 * O nome com que uma pessoa aparece nos `Chip` da caixa do dia e nas linhas da
 * coluna.
 *
 * @param pessoa - Quem está na escala, ou quem folga na escala padrão.
 * @returns O apelido, ou o nome completo de quem não tem apelido.
 */
export function nomeNaEscala(pessoa: {
  nomeCompleto: string;
  apelido: string | null;
}): string {
  return pessoa.apelido ?? pessoa.nomeCompleto;
}

/**
 * O dia preenchido pela escala padrão do dia da semana.
 *
 * As duas listas são trocadas inteiras: quem folga no padrão e está na escala
 * vai para a folga, e, com `plantaoAutomatico`, todo o resto da escala vai para
 * o plantão. Quem estava escalado no dia e não está na escala sai. A situação
 * do dia só muda quando o padrão tem uma; sem ela, a marcada continua.
 *
 * @param padrao - A escala padrão do dia da semana.
 * @param equipe - Quem está na escala da casa, na ordem da coluna.
 * @param status - As situações marcadas no formulário antes do clique.
 * @returns As situações e a escala que o formulário passa a ter.
 */
export function aplicarEscalaPadrao(
  padrao: EscalaPadraoDoDia,
  equipe: readonly PessoaDaEscala[],
  status: readonly StatusDiaCalendario[],
): { status: StatusDiaCalendario[]; escalados: PessoaEscalada[] } {
  const folgam = new Set(padrao.folgas.map((f) => f.id));
  const plantao = padrao.plantaoAutomatico
    ? equipe
        .filter((p) => !folgam.has(p.id))
        .map((p): PessoaEscalada => ({ colaboradorId: p.id, papel: 'plantao' }))
    : [];
  const folga = equipe
    .filter((p) => folgam.has(p.id))
    .map((p): PessoaEscalada => ({ colaboradorId: p.id, papel: 'folga' }));

  return {
    status: padrao.status === null ? [...status] : [padrao.status],
    escalados: [...plantao, ...folga],
  };
}

/**
 * A linha "Padrão: …" que a caixa do dia escreve debaixo da folga.
 *
 * O dia de padrão `todos_residencia` se lê pela situação; os outros, por quem
 * folga e por quem sobra para o plantão. A lista de plantão é escrita mesmo sem
 * `plantaoAutomatico`, porque é a escala que sobra, e não a que o clique monta.
 *
 * @param padrao - A escala padrão do dia da semana, ou `undefined` no dia que
 *   não tem.
 * @param equipe - Quem está na escala da casa, na ordem da coluna.
 * @returns A frase, ou `null` quando o dia não tem padrão a mostrar.
 */
export function linhaDaEscalaPadrao(
  padrao: EscalaPadraoDoDia | undefined,
  equipe: readonly PessoaDaEscala[],
): string | null {
  if (padrao === undefined) return null;
  if (padrao.status === 'todos_residencia') return 'Padrão: Todos na residência';
  if (padrao.folgas.length === 0) return null;

  const folgam = new Set(padrao.folgas.map((f) => f.id));
  const doPlantao = equipe.filter((p) => !folgam.has(p.id)).map(nomeNaEscala);
  return (
    `Padrão: folga de ${padrao.folgas.map(nomeNaEscala).join(', ')}` +
    (doPlantao.length > 0 ? ` · res: ${doPlantao.join(', ')}` : '')
  );
}

/** Um nome sem acento, sem caixa e sem espaço nas pontas, para comparar. */
function semAcento(nome: string): string {
  return nome.normalize('NFD').replace(/\p{M}/gu, '').toLowerCase().trim();
}

/**
 * Se o texto digitado é o nome de uma pessoa.
 *
 * @param pessoa - A pessoa, com o apelido quando houver.
 * @param digitado - O texto do campo.
 * @returns `true` quando o texto é o apelido ou o nome completo dela, sem
 *   contar acento nem caixa.
 */
function eONomeDe(pessoa: PessoaDaEscala, digitado: string): boolean {
  const procurado = semAcento(digitado);
  return (
    semAcento(pessoa.nomeCompleto) === procurado ||
    (pessoa.apelido !== null && semAcento(pessoa.apelido) === procurado)
  );
}

/**
 * Quem do cadastro de pessoal pode entrar na escala da casa.
 *
 * É quem trabalha na casa, não foi desligado e ainda não está na escala. O
 * diarista entra na lista: a escala é de quem a monta, e a origem aceitava
 * qualquer nome.
 *
 * @param colaboradores - O cadastro de pessoal, como `useColaboradores` o traz.
 * @param residenciaId - A casa do calendário aberto.
 * @param naEscala - Os identificadores de colaborador de quem já está nela.
 * @returns Os candidatos, na ordem do cadastro.
 */
export function candidatosAEscala(
  colaboradores: readonly (PessoaDaEscala & { status: string; residencia: { id: string } })[],
  residenciaId: string,
  naEscala: readonly string[],
): PessoaDaEscala[] {
  return colaboradores
    .filter(
      (p) =>
        p.residencia.id === residenciaId &&
        p.status !== 'desligado' &&
        !naEscala.includes(p.id),
    )
    .map((p) => ({ id: p.id, nomeCompleto: p.nomeCompleto, apelido: p.apelido }));
}

/**
 * Quem o nome digitado em "Adicionar nome..." põe na escala.
 *
 * O nome repetido é recusado antes de procurar entre os candidatos, com a
 * frase da origem. O que não é de ninguém da casa é recusado também: a escala
 * se liga por chave, e quem não está no cadastro precisa ser cadastrado no RH.
 *
 * @param digitado - O texto do campo.
 * @param equipe - Quem já está na escala.
 * @param candidatos - Quem pode entrar, de `candidatosAEscala`.
 * @returns A pessoa a pôr na escala, ou a frase do motivo da recusa.
 */
export function quemEntraNaEscala(
  digitado: string,
  equipe: readonly PessoaDaEscala[],
  candidatos: readonly PessoaDaEscala[],
): { pessoa: PessoaDaEscala } | { recusa: string } {
  if (equipe.some((pessoa) => eONomeDe(pessoa, digitado))) {
    return { recusa: 'Esse nome já está na lista.' };
  }
  const pessoa = candidatos.find((candidato) => eONomeDe(candidato, digitado));
  return pessoa === undefined
    ? { recusa: 'Ninguém com esse nome no cadastro de pessoal desta casa.' }
    : { pessoa };
}
